import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import API from '../utils/api';
import toast from 'react-hot-toast';

const fadeIn = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

// ===== SUB-COMPONENTS =====

function Overview({ user, requests }) {
  const openCount = requests.filter(r => r.status === 'open').length;
  const inProgressCount = requests.filter(r => r.status === 'in_progress').length;
  const resolvedCount = requests.filter(r => r.status === 'resolved').length;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="stats-row">
        {[
          { icon: '📝', label: 'Total Tasks', value: requests.length, cls: 'blue' },
          { icon: '🔓', label: 'Open', value: openCount, cls: 'yellow' },
          { icon: '⚡', label: 'In Progress', value: inProgressCount, cls: 'purple' },
          { icon: '✅', label: 'Resolved', value: resolvedCount, cls: 'green' },
        ].map((s, i) => (
          <motion.div key={i} className="stat-card" whileHover={{ y: -4 }}>
            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
            <div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Plan info */}
      {user?.plan_id && (
        <div className="limit-bar">
          <div className="limit-bar-top">
            <span style={{ fontWeight: 600, fontSize: 13 }}>📊 Task Usage</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{user.request_count} used</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${Math.min(100, (user.request_count / 20) * 100)}%` }} />
          </div>
          {user.plan_expires_at && (
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
              Plan expires: {new Date(user.plan_expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Recent requests */}
      <div className="table-card">
        <div className="table-card-header">
          <h3>Recent Tasks</h3>
        </div>
        {requests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No tasks yet</h3>
            <p>Create your first DevOps support task</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {requests.slice(0, 5).map(r => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.category}</td>
                  <td><span className={`priority-${r.priority}`} style={{ textTransform: 'capitalize', fontWeight: 600 }}>{r.priority}</span></td>
                  <td><span className={`status-badge status-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-muted)' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
}

function RequestsList({ requests, onViewRequest }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header">
          <h3>My Tasks ({filtered.length})</h3>
          <select className="form-control" style={{ width: 160, padding: '8px 12px', fontSize: 13 }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No tasks found</h3>
            <p>Create a new task to get expert help</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Title</th><th>Category</th><th>Priority</th><th>Status</th><th>Date</th><th>Action</th></tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id}>
                  <td>{r.title}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{r.category}</td>
                  <td><span className={`priority-${r.priority}`} style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.priority}</span></td>
                  <td><span className={`status-badge status-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => onViewRequest(r)}>View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
}

function NewRequest({ onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', category: 'Docker', priority: 'medium' });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const CATEGORIES = ['Docker', 'Kubernetes', 'AWS', 'Terraform', 'Jenkins', 'GitHub Actions', 'Ansible', 'Prometheus', 'Grafana', 'Linux', 'CI/CD', 'Networking', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) return toast.error('Title and description required');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      await API.post('/requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('✅ Request created! Our experts will respond soon.');
      setForm({ title: '', description: '', category: 'Docker', priority: 'medium' });
      setFile(null);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="glass-card" style={{ maxWidth: 700 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>📝 Create New Support Task</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Task Title *</label>
            <input className="form-control" placeholder="e.g., Kubernetes pod crashing with OOMKilled error" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-control" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🔴 High</option>
                <option value="urgent">🚨 Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Detailed Description *</label>
            <textarea
              className="form-control"
              style={{ minHeight: 160 }}
              placeholder="Describe your issue in detail. Include error messages, steps to reproduce, what you've already tried..."
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Attach File (optional)</label>
            <div
              className="file-upload-zone"
              onClick={() => document.getElementById('req-file').click()}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>📁</div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                {file ? `✅ ${file.name}` : 'Click or drag to attach a file (logs, configs, screenshots)'}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>Max 10MB</p>
            </div>
            <input id="req-file" type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading} style={{ minWidth: 200 }}>
            {loading ? '⏳ Submitting...' : '🚀 Submit Task'}
          </button>
        </form>
      </div>
    </motion.div>
  );
}

function RequestDetail({ request, onBack }) {
  const { user } = useAuth();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    API.get(`/requests/${request.id}`)
      .then(res => setResponses(res.data.responses))
      .finally(() => setLoading(false));
  }, [request.id]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('message', reply);
      if (file) fd.append('file', file);
      const res = await API.post(`/requests/${request.id}/respond`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResponses(prev => [...prev, res.data.response]);
      setReply('');
      setFile(null);
      toast.success('Reply sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={onBack}>← Back to Tasks</button>

      <div className="request-detail">
        <div className="glass-card" style={{ marginBottom: 24 }}>
          <div className="request-header">
            <h2>{request.title}</h2>
            <div className="request-meta">
              <span className={`status-badge status-${request.status}`}>{request.status.replace('_', ' ')}</span>
              <span className={`priority-${request.priority}`} style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: 13 }}>
                ● {request.priority} priority
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>#{request.id} · {new Date(request.created_at).toLocaleString()}</span>
            </div>
          </div>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginTop: 16 }}>{request.description}</p>
          {request.file_name && (
            <div style={{ marginTop: 16 }}>
              <a href={`http://localhost:5000/uploads/${request.file_path}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                📎 {request.file_name}
              </a>
            </div>
          )}
        </div>

        {/* Responses */}
        {loading ? (
          <div className="loading-container"><div className="loading-spinner" /></div>
        ) : (
          <div className="response-list">
            {responses.map(r => (
              <motion.div
                key={r.id}
                className={`response-item ${r.is_admin_response ? 'admin-response' : ''}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="response-author">
                  <div className={`response-avatar ${r.is_admin_response ? '' : 'user-avatar'}`}>
                    {r.author_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="response-name">
                      {r.author_name}
                      {r.is_admin_response && <span style={{ marginLeft: 8, background: 'rgba(99,102,241,0.15)', color: 'var(--brand-primary)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Admin</span>}
                    </div>
                    <div className="response-time">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <p className="response-message">{r.message}</p>
                {r.file_name && (
                  <a href={`http://localhost:5000/uploads/${r.file_path}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}>
                    📎 {r.file_name}
                  </a>
                )}
              </motion.div>
            ))}

            {/* Reply form */}
            {request.status !== 'closed' && (
              <div className="glass-card">
                <h4 style={{ marginBottom: 16, fontSize: 15, fontWeight: 700 }}>💬 Add Follow-up</h4>
                <form onSubmit={sendReply}>
                  <div className="form-group">
                    <textarea className="form-control" placeholder="Add more details or follow-up information..." value={reply} onChange={e => setReply(e.target.value)} style={{ minHeight: 100 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => document.getElementById('reply-file').click()}>
                      📎 {file ? file.name : 'Attach File'}
                    </button>
                    <input id="reply-file" type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                    <button type="submit" className="btn btn-primary btn-sm" disabled={sending}>
                      {sending ? '⏳ Sending...' : '📤 Send Reply'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Profile({ user, onRefresh }) {
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.put('/auth/profile', form);
      toast.success('Profile updated!');
      onRefresh();
    } catch { toast.error('Failed to update'); } finally { setLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) return toast.error('Passwords do not match');
    if (pwForm.newPassword.length < 6) return toast.error('Password must be at least 6 chars');
    setPwLoading(true);
    try {
      await API.put('/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); } finally { setPwLoading(false); }
  };

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
      <div className="glass-card">
        <h3 style={{ marginBottom: 24, fontSize: 16, fontWeight: 700 }}>👤 Profile Information</h3>
        <form onSubmit={updateProfile}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-control" value={user?.email} disabled style={{ opacity: 0.6 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? 'Saving...' : '💾 Save Changes'}
          </button>
        </form>
      </div>

      <div className="glass-card">
        <h3 style={{ marginBottom: 24, fontSize: 16, fontWeight: 700 }}>🔒 Change Password</h3>
        <form onSubmit={changePassword}>
          {['currentPassword', 'newPassword', 'confirmPassword'].map((field, i) => (
            <div className="form-group" key={field}>
              <label className="form-label">{['Current Password', 'New Password', 'Confirm Password'][i]}</label>
              <input className="form-control" type="password" value={pwForm[field]} onChange={e => setPwForm({ ...pwForm, [field]: e.target.value })} required />
            </div>
          ))}
          <button type="submit" className="btn btn-secondary btn-sm" disabled={pwLoading}>
            {pwLoading ? 'Updating...' : '🔑 Update Password'}
          </button>
        </form>
      </div>
    </motion.div>
  );
}

function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/notifications').then(res => setNotifs(res.data.notifications)).finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await API.put(`/notifications/${id}/read`);
    setNotifs(notifs.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;

  return (
    <motion.div variants={fadeIn} initial="hidden" animate="show">
      <div className="table-card">
        <div className="table-card-header"><h3>🔔 Notifications ({notifs.filter(n => !n.is_read).length} unread)</h3></div>
        {notifs.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🔔</div><h3>No notifications</h3></div>
        ) : (
          notifs.map(n => (
            <div key={n.id} className={`notif-item ${!n.is_read ? 'unread' : ''}`} onClick={() => !n.is_read && markRead(n.id)}
              style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: n.is_read ? 'default' : 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div className="notif-title" style={{ marginBottom: 4 }}>{n.title}</div>
                  <div className="notif-msg">{n.message}</div>
                </div>
                {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-primary)', flexShrink: 0, marginTop: 4 }} />}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 6 }}>{new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}

// ===== MAIN DASHBOARD =====
export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin') return <Navigate to="/admin" />;

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = () => {
    API.get('/requests').then(res => setRequests(res.data.requests));
  };

  const handleViewRequest = (req) => {
    setSelectedRequest(req);
    setActiveTab('request-detail');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <Overview user={user} requests={requests} />;
      case 'requests': return selectedRequest && activeTab === 'request-detail'
        ? <RequestDetail request={selectedRequest} onBack={() => { setSelectedRequest(null); setActiveTab('requests'); }} />
        : <RequestsList requests={requests} onViewRequest={handleViewRequest} />;
      case 'request-detail': return <RequestDetail request={selectedRequest} onBack={() => { setSelectedRequest(null); setActiveTab('requests'); }} />;
      case 'new-request': return <NewRequest onCreated={() => { loadRequests(); setActiveTab('requests'); }} />;
      case 'notifications': return <Notifications />;
      case 'profile': return <Profile user={user} onRefresh={refreshUser} />;
      default: return <Overview user={user} requests={requests} />;
    }
  };

  const titles = {
    overview: '📊 Overview',
    requests: '📝 My Tasks',
    'new-request': '➕ New Task',
    'request-detail': '📋 Task Details',
    notifications: '🔔 Notifications',
    profile: '👤 Profile',
  };

  return (
    <div className="dashboard-layout">
      <Sidebar
        active={activeTab === 'request-detail' ? 'requests' : activeTab}
        onNavigate={(tab) => { setActiveTab(tab); setSelectedRequest(null); }}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <main className="dashboard-main">
        <div className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              style={{ display: 'none', background: 'var(--bg-glass)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 12px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 18 }}
              id="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
            >
              ☰
            </button>
            <h1 className="dashboard-title">{titles[activeTab] || 'Dashboard'}</h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {user.plan_id ? '✅ Active Plan' : '⚠️ No Active Plan'}
            </span>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setActiveTab('new-request')}
            >
              ➕ New Task
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          <AnimatePresence mode="wait">
            <div key={activeTab}>
              {renderContent()}
            </div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
